import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, DOCS_URL, ErrorState, PageHead, Pill, Skeleton, dateTime, time } from '@fa/ui';
import { ApiError, api } from '../lib/api.js';
import type { ApiKeyView, DeliveryView, WebhookView } from '../lib/types.js';

/**
 * Integração: chaves de API e webhooks.
 *
 * Duas telas em uma porque quem integra faz as duas coisas na mesma sessão:
 * cria a chave, cadastra o endereço de retorno e confere as entregas.
 * O segredo — da chave e do webhook — aparece uma única vez, na criação.
 */
export function Integration(): ReactNode {
  const queryClient = useQueryClient();
  const [newSecret, setNewSecret] = useState<{ label: string; value: string } | null>(null);

  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api<{ data: ApiKeyView[] }>('/v1/api-keys'),
  });
  const hooks = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api<{ data: WebhookView[] }>('/v1/webhooks'),
  });
  const deliveries = useQuery({
    queryKey: ['deliveries'],
    queryFn: () => api<{ data: DeliveryView[] }>('/v1/webhooks/deliveries?limit=20'),
    refetchInterval: 30_000,
  });

  const createKey = useMutation({
    mutationFn: (name: string) =>
      api<{ key: string }>('/v1/api-keys', {
        method: 'POST',
        body: { name, environment: 'sandbox', scopes: ['*'] },
      }),
    onSuccess: (data) => {
      setNewSecret({ label: 'Chave de API', value: data.key });
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) => api(`/v1/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const criarHook = useMutation({
    mutationFn: (url: string) =>
      api<{ secret: string }>('/v1/webhooks', { method: 'POST', body: { url, events: [] } }),
    onSuccess: (data) => {
      // O segredo aparece uma unica vez; sem ele o cliente nao consegue
      // conferir a assinatura das entregas.
      setNewSecret({ label: 'Segredo do webhook', value: data.secret });
      void queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });

  const retry = useMutation({
    mutationFn: (id: string) => api(`/v1/webhooks/deliveries/${id}/retry`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['deliveries'] }),
  });

  return (
    <>
      <PageHead
        title="Integração"
        subtitle="Chaves de acesso e notificações para o seu ERP ou PDV"
        actions={
          <a
            className="btn btn-ghost"
            href={DOCS_URL}
            target="_blank"
            rel="noreferrer"
          >
            Abrir documentação
          </a>
        }
      />

      {newSecret && (
        <div className="alert warn">
          <b>{newSecret.label} criada — copie agora</b>
          Este valor não será exibido novamente. Guarde-o no cofre de segredos do seu sistema.
          <div className="fix">
            <code className="code-inline" style={{ userSelect: 'all' }}>
              {newSecret.value}
            </code>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => void navigator.clipboard?.writeText(newSecret.value)}
            >
              Copiar
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setNewSecret(null)}>
              Já guardei
            </button>
          </div>
        </div>
      )}

      {createKey.isError && (
        <div className="alert bad">
          <b>Não foi possível criar a chave</b>
          {createKey.error instanceof ApiError ? createKey.error.message : 'Tente novamente.'}
        </div>
      )}

      <div className="grid-2">
        <Card
          title="Chaves de API"
          padded={false}
          action={
            <button
              className="btn btn-sm btn-primary"
              disabled={createKey.isPending}
              onClick={() => {
                const name = window.prompt('Nome da chave (ex.: PDV lojas)');
                if (name) createKey.mutate(name);
              }}
            >
              {createKey.isPending ? 'Criando...' : 'Criar chave'}
            </button>
          }
        >
          {keys.isLoading && <Skeleton rows={3} />}
          {keys.isError && <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />}
          {keys.data && (
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Chave</th>
                  <th className="r">Último uso</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.data.data.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <b>{key.name}</b>
                      <span className="sub">
                        {key.environment === 'production' ? 'produção' : 'sandbox'} ·{' '}
                        {key.scopes.includes('*') ? 'acesso total' : key.scopes.join(', ')}
                      </span>
                    </td>
                    <td className="key" style={{ fontSize: '.78rem' }}>
                      {key.prefix.slice(0, 16)}…
                    </td>
                    <td className="r num" style={{ fontSize: '.82rem' }}>
                      {key.last_used_at ? dateTime(key.last_used_at) : 'nunca'}
                    </td>
                    <td className="r">
                      {key.revoked_at ? (
                        <Pill tone="mute">Revogada</Pill>
                      ) : (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Revogar a chave "${key.name}"? Integrações que a usam param de funcionar.`,
                              )
                            ) {
                              revokeKey.mutate(key.id);
                            }
                          }}
                        >
                          Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {keys.data.data.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                      Nenhuma chave criada. Crie uma para o seu ERP começar a emitir.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Notificações (webhooks)"
          action={
            <button
              className="btn btn-sm btn-primary"
              disabled={criarHook.isPending}
              onClick={() => {
                const url = window.prompt('Endereço HTTPS que receberá os eventos:');
                if (url) criarHook.mutate(url);
              }}
            >
              {criarHook.isPending ? 'Criando...' : 'Adicionar endereço'}
            </button>
          }
        >
          <div style={{ display: 'grid', gap: 14 }}>
            {hooks.isLoading && <Skeleton rows={2} />}
            {hooks.data?.data.map((hook) => (
              <div key={hook.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span className={`toggle${hook.active ? '' : ' off'}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b
                    className="key"
                    style={{
                      fontWeight: 600,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: hook.active ? undefined : 'var(--ink-3)',
                    }}
                  >
                    {hook.url}
                  </b>
                  <div style={{ color: 'var(--ink-3)', fontSize: '.85rem', marginTop: 2 }}>
                    {hook.events.length === 0 ? 'todos os eventos' : `${hook.events.length} evento(s)`} ·{' '}
                    {hook.last_7_days.delivered} entregas em 7 dias
                    {hook.last_7_days.failed > 0 && ` · ${hook.last_7_days.failed} falha(s)`}
                  </div>
                </div>
              </div>
            ))}
            {hooks.data?.data.length === 0 && (
              <div style={{ color: 'var(--ink-3)' }}>
                Nenhum endereço cadastrado. Sem webhook, o seu sistema precisa consultar o status de cada nota
                — o que gasta requisições e atrasa a resposta ao caixa.
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card
        title="Entregas recentes"
        padded={false}
        action={
          <Pill tone={(deliveries.data?.data ?? []).some((d) => d.status !== 'delivered') ? 'warn' : 'ok'}>
            {(deliveries.data?.data ?? []).some((d) => d.status !== 'delivered')
              ? 'Há entregas pendentes'
              : 'Tudo entregue'}
          </Pill>
        }
      >
        {deliveries.isLoading && <Skeleton rows={4} />}
        {deliveries.data && (
          <table>
            <thead>
              <tr>
                <th>Evento</th>
                <th>Endereço</th>
                <th>Resposta</th>
                <th className="r">Horário</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {deliveries.data.data.map((delivery) => (
                <tr key={delivery.id}>
                  <td className="key">{delivery.event}</td>
                  <td className="key" style={{ fontSize: '.78rem' }}>
                    {hostOf(delivery.url)}
                  </td>
                  <td>
                    <Pill
                      tone={
                        delivery.status === 'delivered'
                          ? 'ok'
                          : delivery.status === 'dead'
                            ? 'bad'
                            : delivery.status === 'failed'
                              ? 'warn'
                              : 'info'
                      }
                    >
                      {delivery.status === 'delivered'
                        ? `${delivery.status_code} · ${delivery.response_ms ?? 0} ms`
                        : delivery.status === 'dead'
                          ? 'desistimos após 6 tentativas'
                          : delivery.status === 'failed'
                            ? `tentativa ${delivery.attempt} · ${delivery.error ?? 'falhou'}`
                            : 'na fila'}
                    </Pill>
                  </td>
                  <td className="r num">{time(delivery.created_at)}</td>
                  <td className="r">
                    <button
                      className="btn btn-sm btn-ghost"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(delivery.id)}
                    >
                      Reenviar
                    </button>
                  </td>
                </tr>
              ))}
              {deliveries.data.data.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                    Nenhuma entrega ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};
